import React, { useState } from "react";
import {
  Box,
  Collapse,
  Container,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton,
} from "@mui/material";
import {
  Menu as MenuIcon,
  HomeOutlined as HomeRoundedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ReceiptRounded as ReceiptRoundedIcon,
  ArticleOutlined as ClaimIcon,
  PaidOutlined as PaymentIcon,
  HistoryOutlined as PaymentHistoryIcon,
} from "./icons";
import logo from "../assets/images/logo.svg";
import { string } from "../constants/string";
import { useUserStore } from "../store/useUserStore";
import { isIfasDriverRole, isIfasRole } from "../utils/portal";
import { canAccessIfasModule } from "../utils/ifasAccess";
import { useSessionStore } from "../store/useSessionStore";
import ClaimsNav from "./nav/ClaimsNav";
import IfasNav from "./nav/IfasNav";

export type SidebarProp = {
  onDeathClaimClick?: () => void;
  onTenPercentClick?: () => void;
  onHomeButtonClick?: () => void;
  onPaymentProceedClicked?: () => void;
  onPaymentHistoryClicked?: () => void;
  onCasePortalClicked?: () => void;
  onFuneralPlanningClicked?: () => void;
  onSpecialServicesClicked?: () => void;
  onCateringPortalClicked?: () => void;
  onBodyRemovalClicked?: () => void;
  onMortuaryClicked?: () => void;
  onWarehouseClicked?: () => void;
  onBodyDispatchClicked?: () => void;
  onQuotationClicked?: () => void;
  onRevenueClicked?: () => void;
  onCatalogueClicked?: () => void;
  onMemorialsPortalClicked?: () => void;
  onTombstonePortalClicked?: () => void;
  onFleetClicked?: () => void;
  onDriversClicked?: () => void;
  onVehiclesClicked?: () => void;
  onIfasReportsClicked?: () => void;
  setShowSearchInput: (value: boolean) => void;
  setShowFilterIcon: (value: boolean) => void;
  onMonthlyReportsClicked?: () => void;
  onAgedClicked?: () => void;
  onDateToDateClaimsClick?: () => void;
  onExecutiveReportsClick?: () => void;
  showHamburger?: boolean;
};

export default function Sidebar(props: SidebarProp) {
  const {
    onDeathClaimClick,
    onHomeButtonClick,
    onPaymentProceedClicked,
    onPaymentHistoryClicked,
    setShowSearchInput,
    setShowFilterIcon,
    showHamburger
  } = props;

  const [financeOpen, setFinanceOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleFinanceToggle = () => setFinanceOpen(!financeOpen);

  const role = useUserStore((s) => s.role);
  const { userRole } = useSessionStore();
  const effectiveRole = userRole || role;
  const isUnderwriter = String(effectiveRole).toLowerCase() === "underwriter";
  const isIfasUser = isIfasRole(effectiveRole);
  const canSeeClaims = !isIfasUser;
  const canSeeIfas = isIfasUser;
  const canSeeCasePortal = canAccessIfasModule(effectiveRole, "casePortal");
  const canSeeBodyRemoval = canAccessIfasModule(effectiveRole, "bodyRemoval");
  const canSeeMortuary = canAccessIfasModule(effectiveRole, "mortuary");
  const canSeeWarehouse = canAccessIfasModule(effectiveRole, "warehouse");
  const canSeeBodyDispatch = canAccessIfasModule(effectiveRole, "bodyDispatch");
  const canSeeQuotations = canAccessIfasModule(effectiveRole, "quotations");
  const canSeeMemorials = canAccessIfasModule(effectiveRole, "memorials");
  const canSeeRevenue = canAccessIfasModule(effectiveRole, "revenue");
  const canSeeCatalogue = canAccessIfasModule(effectiveRole, "catalogue");
  // Revenue and Catalogue live inside this section, so they have to be able to
  // open it on their own. The GCOO and GCEO offices hold revenue and nothing
  // else here: without them listed, canSeeRevenue was true while the section
  // wrapping it never rendered, and the AoD approvals screen was unreachable.
  const canSeeFuneralSection =
    canSeeCasePortal ||
    canSeeBodyRemoval ||
    canSeeMortuary ||
    canSeeWarehouse ||
    canSeeBodyDispatch ||
    canSeeQuotations ||
    canSeeMemorials ||
    canSeeRevenue ||
    canSeeCatalogue;

  // Drawer content (can reuse for desktop if needed)
  const sidebarContent = (
    <Box
      sx={{
        // Responsive pass (F.9): this same element is the mobile Drawer's
        // content AND the docked desktop nav. `{ lg: 280 }` alone left it
        // width-less between 900 and 1199 (tablet landscape) and inside the
        // Drawer, where it shrink-wrapped to whatever the longest nav label
        // happened to be. Every tier is explicit now; the Drawer keeps a
        // dismiss scrim on a 390px handheld.
        width: { xs: 248, md: 240, lg: 280 },
        flexShrink: 0,
        backgroundColor: "background.default",
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid #E6E2DC",
        minHeight: "100vh",
      }}
    >
      <Container
        disableGutters
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2.5,
          pt: 2.5,
          pb: 1.5,
        }}
      >
        <img src={logo} alt="Logo" style={{ maxHeight: 50 }} />
      </Container>

      <List
        component="nav"
        sx={{
          px: 2,
          pt: 1,
          pb: 2,
          "& .MuiListItemButton-root": {
            borderRadius: 2,
            minHeight: 46,
            mb: 0.35,
            transition: "background-color 160ms ease, color 160ms ease",
          },
          "& .MuiListItemButton-root:hover": {
            bgcolor: "#FFF4EB",
          },
          "& .MuiListItemIcon-root": {
            minWidth: 42,
          },
          "& .MuiListItemText-primary": {
            fontSize: 15,
            lineHeight: 1.25,
          },
          "& .MuiCollapse-root .MuiListItemButton-root": {
            minHeight: 42,
            borderRadius: 1.5,
          },
        }}
      >
        {/* Home */}
        <ListItemButton
          onClick={() => {
            onHomeButtonClick?.();
            setDrawerOpen(false);
          }}
          sx={{
            bgcolor: "primary.main",
            height: "50px",
            mb: 1,
            transition: "all 0.3s ease",

            "&:hover": {
              bgcolor: "rgba(236, 110, 0, 0.92) !important",

              "& .MuiTypography-root": {
                color: "#ffff !important",
              },

              "& .MuiListItemIcon-root": {
                 color: "#ffff !important",
              },

              "& .MuiSvgIcon-root": {
                color: "#ffff !important",
              },
            },
            "&:active": {
              bgcolor: "rgba(236, 110, 0, 0.82) !important",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 46 }}>
            <HomeRoundedIcon
              sx={{
                color: "primary.contrastText",
                fontSize: "30px",
                opacity: 0.9,
                transition: "color 0.3s ease",
              }}
            />
          </ListItemIcon>

          <ListItemText
            primary={string.sidebar.home}
            slotProps={{
              primary: {
                sx: {
                  color: "primary.contrastText",
                  fontWeight: 500,
                  transition: "color 0.3s ease",
                },
              },
            }}
          />
        </ListItemButton>

        {isUnderwriter ? (
          <ListItemButton
            onClick={() => {
              onDeathClaimClick?.();
              setDrawerOpen(false);
            }}
          >
            <ListItemIcon sx={{ minWidth: 46 }}>
              <ClaimIcon
                sx={{
                  color: "rgba(0, 0, 0, 0.62)",
                  fontSize: "26px",
                  ml: 0.5,
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={string.sidebar.deathClaim}
              slotProps={{
                primary: { sx: { color: "text.primary", fontWeight: 500 } },
              }}
            />
          </ListItemButton>
        ) : (
          <>
        {canSeeClaims && (
          <ClaimsNav
            props={props}
            effectiveRole={effectiveRole}
            closeDrawer={() => setDrawerOpen(false)}
          />
        )}
        {canSeeIfas && (
          <IfasNav
            props={props}
            effectiveRole={effectiveRole}
            closeDrawer={() => setDrawerOpen(false)}
          />
        )}
          </>
        )}




        {/* Reports Section - Acts ONLY as a dropdown toggle */}

        {effectiveRole === "finance" && (
          <ListItemButton onClick={handleFinanceToggle}>
            <ListItemIcon sx={{ minWidth: 46 }}>
              <ReceiptRoundedIcon
                sx={{
                  color: "grey",
                  fontSize: "30px",
                  opacity: 0.9,
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={string.sidebar.finance}
              slotProps={{
                primary: { sx: { color: "text.primary", fontWeight: 500 } },
              }}
            />
            {financeOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        )}

        <Collapse in={financeOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ ml: 6, borderBottom: "2px solid #DFDDDD" }}
              onClick={() => {
                onPaymentProceedClicked?.();
                setShowSearchInput(false);
                setShowFilterIcon(false);
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PaymentIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.62)",
                    fontSize: "22px",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={string.sidebar.paymentProceed}
                slotProps={{
                  primary: { sx: { color: "text.primary", fontWeight: 500 } },
                }}
              />
            </ListItemButton>
            <ListItemButton
              sx={{ ml: 6 }}
              onClick={() => {
                onPaymentHistoryClicked?.();
                setShowSearchInput(false);
                setShowFilterIcon(false);
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PaymentHistoryIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.62)",
                    fontSize: "22px",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={string.sidebar.history}
                slotProps={{
                  primary: { sx: { color: "text.primary", fontWeight: 500 } },
                }}
              />
            </ListItemButton>
          </List>
        </Collapse>
      </List>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          p: 1,
          alignItems: "center",
        }}
      >
        {showHamburger && (
        <IconButton
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          sx={{ width: 44, height: 44 }}
        >
          <MenuIcon />
        </IconButton>
        )}
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {sidebarContent}
      </Drawer>

      <Box sx={{ display: { xs: "none", md: "block" } }}>{sidebarContent}</Box>
    </>
  );
}
